'use client'

import { Button, FormField, Input, LinkButton, Text } from '@/components'
import { ClientRoutes } from '@/config'
import { Config } from '@/config/config'
import { useLocalizedRoutes } from '@/lib/hooks'
import { useAppStore, appSelectors, MessageSeverity } from '@/store'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import React, { useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { InferType, object, string } from 'yup'

enum SignInFields {
  email = 'email',
  password = 'password',
}

const signInFormSchema = object({
  [SignInFields.email]: string().email().required(),
  [SignInFields.password]: string().required().min(6),
})

export type SignInFormSchemaType = InferType<typeof signInFormSchema>

export interface SignInFormProps {
  redirectAfterLogin?: string
  onFinish?: () => void | Promise<void>
  showOptions?: boolean
}

export const SignInForm: React.FC<SignInFormProps> = ({
  redirectAfterLogin,
  onFinish,
  showOptions = true,
}) => {
  const signIn = useAppStore(appSelectors.signIn)
  const { setMessage } = useAppStore(appSelectors.actions)
  const { isLoggedIn } = useAppStore(appSelectors.auth)
  const { push } = useRouter()

  const form = useForm({
    mode: 'onSubmit',
    shouldUnregister: true,
    resolver: yupResolver(signInFormSchema),
  })

  const t = useTranslations('auth')
  const { get: _ } = useLocalizedRoutes()

  const { control, handleSubmit } = form

  const submit = useCallback(() => {
    handleSubmit(
      async (data) => {
        await signIn(data.email, data.password, onFinish)
      },
      (err) => {
        console.log('signIn failed', err)
        setMessage({
          message: t('signIn.failed'),
          severity: MessageSeverity.ERROR,
        })
      },
    )()
  }, [handleSubmit, onFinish, setMessage, signIn, t])

  useEffect(() => {
    if (redirectAfterLogin && isLoggedIn) {
      push(redirectAfterLogin)
    }
  }, [isLoggedIn, push, redirectAfterLogin])

  return (
    <div className="w-full flex-1">
      <div className="flex flex-col gap-4 w-full mt-5 text-center">
        <h1 className="text-white text-4xl my-8">{t('signIn.title')}</h1>
        <FormProvider {...form}>
          <FormField
            control={control}
            name={SignInFields.email}
            render={({ field }) => (
              <Input
                type="email"
                {...field}
                placeholder={t('fields.email')}
                className="px-6 py-4 h-12 w-full text-xl rounded-full text-base placeholder:text-base bg-primary text-white border-white"
              />
            )}
          />

          <FormField
            control={control}
            name={SignInFields.password}
            render={({ field }) => (
              <Input
                type="password"
                {...field}
                placeholder={t('fields.password')}
                className="px-6 py-4 h-12 w-full text-xl rounded-full text-base placeholder:text-base bg-primary text-white border-white"
              />
            )}
          />
        </FormProvider>

        <Button
          onClick={submit}
          type="button"
          size="lg"
          variant="glow"
          className="w-auto p-6 text-lg my-3 2xl:mt-12 mb-6 rounded-full text-primary"
        >
          {t('actions.signIn')}
        </Button>

        {Config.FEATURES?.SIGNUP_ENABLED && showOptions ? (
          <p>
            <>
              <Text variant="heading">{t('signInForm.dontHaveAccount')}</Text>
              <div>
                <LinkButton
                  href={_(ClientRoutes.signUp)}
                  className="text-white text-lg rounded-full my-5 px-8 py-6"
                  variant="outline"
                  size="lg"
                >
                  {t('actions.signUp')}
                </LinkButton>
              </div>
            </>
          </p>
        ) : null}
      </div>
    </div>
  )
}
